import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useTreasuryYields — Phase 6 (Jun 2026)
 *
 * Tiny shared hook that fetches the latest Treasury yield curve
 * snapshot from `/api/macro/treasury-yields`. The endpoint reads
 * from the `macro_yields` collection populated by the
 * `treasury-yields` daily cron.
 *
 * Caches the response in a module-level variable so the curve
 * doesn't get refetched on every modal open / portfolio view.
 * The curve only changes once per day (Fed publishes once daily,
 * cron pulls overnight), so a process-lifetime cache is fine.
 *
 * Returns `null` until the first fetch completes.
 */
let _cachedCurve = null;
let _inflight = null;

export function useTreasuryYields() {
  const [curve, setCurve] = useState(_cachedCurve);

  useEffect(() => {
    if (_cachedCurve) return undefined;
    if (!_inflight) {
      _inflight = axios
        .get(`${API}/macro/treasury-yields`, { withCredentials: true })
        .then((r) => {
          _cachedCurve = r.data || null;
          return _cachedCurve;
        })
        .catch(() => {
          _cachedCurve = null;
          return null;
        })
        .finally(() => { _inflight = null; });
    }
    let cancelled = false;
    _inflight.then((c) => { if (!cancelled) setCurve(c); });
    return () => { cancelled = true; };
  }, []);

  return curve;
}
